import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { X, Truck } from 'lucide-react';
import { Button } from '../../../../components/ui/Button';
import { createCourierThunk } from '../../../../features/couriers/couriersThunks';
import { selectIsRunning } from '../../../../features/feedback/feedbackSlice';

const DEFAULT_FORM_STATE = {
  name: "",
  configuration: {},
  services: {},
};

const COURIER_SERVICES_TEMPLATES = {
  FEDEX: {
    FEDEX_PRIORITY: { min_weight: "0", max_weight: "" },
    FEDEX_PRIORITY_FREIGHT: { min_weight: "", max_weight: "" },
  },
};

export const CreateCourierModal = ({ isOpen, onClose }) => {
  const dispatch = useDispatch();
  const { templates } = useSelector((state) => state.couriers);
  // The save itself, not the slice's `loading`: that one is also on while the
  // list behind the dialog reloads.
  const saving = useSelector(selectIsRunning(createCourierThunk));

  const [formData, setFormData] = useState(DEFAULT_FORM_STATE);
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    if (isOpen) {
      setFormData(DEFAULT_FORM_STATE);
      setFormErrors({});
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const isFormValid =
    formData.name.trim() !== "" &&
    Object.keys(templates[formData.name] || {}).every(
      (key) => formData.configuration[key] && formData.configuration[key].trim() !== ""
    ) &&
    (formData.name !== "FEDEX" ||
      Object.keys(formData.services).every(
        (serviceKey) =>
          formData.services[serviceKey].min_weight !== "" &&
          formData.services[serviceKey].max_weight !== ""
      ));

  const handleCourierChange = (e) => {
    const courierName = e.target.value;
    const initialConfig = templates[courierName] ? { ...templates[courierName] } : {};

    const initialServices = COURIER_SERVICES_TEMPLATES[courierName]
      ? JSON.parse(JSON.stringify(COURIER_SERVICES_TEMPLATES[courierName]))
      : {};

    setFormData({
      name: courierName,
      configuration: initialConfig,
      services: initialServices,
    });
    setFormErrors((prev) => ({ ...prev, name: null }));
  };

  const handleConfigChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      configuration: { ...prev.configuration, [name]: value },
    }));
    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const handleServiceWeightChange = (serviceKey, field, value) => {
    const sanitizedValue = value.replace(/-/g, "");
    setFormData((prev) => ({
      ...prev,
      services: {
        ...prev.services,
        [serviceKey]: {
          ...prev.services[serviceKey],
          [field]: sanitizedValue,
        },
      },
    }));

    if (formErrors[`${serviceKey}_${field}`]) {
      setFormErrors((prev) => ({ ...prev, [`${serviceKey}_${field}`]: null }));
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.name) {
      errors.name = "El proveedor es requerido";
      setFormErrors(errors);
      return false;
    }

    const requiredFields = Object.keys(templates[formData.name] || {});
    requiredFields.forEach((field) => {
      if (!formData.configuration[field] || formData.configuration[field].trim() === "") {
        errors[field] = "Este campo es requerido";
      }
    });

    if (formData.name === "FEDEX") {
      const priorityMin = parseFloat(formData.services.FEDEX_PRIORITY?.min_weight);
      const priorityMax = parseFloat(formData.services.FEDEX_PRIORITY?.max_weight);
      const freightMin = parseFloat(formData.services.FEDEX_PRIORITY_FREIGHT?.min_weight);
      const freightMax = parseFloat(formData.services.FEDEX_PRIORITY_FREIGHT?.max_weight);

      if (isNaN(priorityMin) || priorityMin < 0) errors.FEDEX_PRIORITY_min_weight = "Mínimo inválido";
      if (isNaN(priorityMax) || priorityMax <= priorityMin) errors.FEDEX_PRIORITY_max_weight = "Debe ser mayor al mínimo";
      if (isNaN(freightMin) || freightMin < 0) errors.FEDEX_FREIGHT_min_weight = "Mínimo inválido";
      if (isNaN(freightMax) || freightMax <= freightMin) errors.FEDEX_FREIGHT_max_weight = "Debe ser mayor al mínimo";

      if (!errors.FEDEX_PRIORITY_max_weight && !errors.FEDEX_FREIGHT_min_weight) {
        if (
          (priorityMin <= freightMin && priorityMax >= freightMin) ||
          (freightMin <= priorityMin && freightMax >= priorityMin)
        ) {
          errors.submit = "Restricción de Arquitectura: Los rangos de peso no pueden solaparse ni tener valores intermedios iguales.";
        }
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    // Remove services if empty
    const payload = {
      name: formData.name,
      configuration: formData.configuration,
      ...(Object.keys(formData.services).length > 0 && { services: formData.services })
    };

    dispatch(createCourierThunk({ courierData: payload }))
      .unwrap()
      .then(() => {
        onClose();
      })
      .catch((err) => {
        console.error("Error al crear:", err);
      });
  };

  const formatLabel = (key) => {
    return key
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const isSecretField = (key) => {
    const lowerKey = key.toLowerCase();
    return lowerKey.includes("secret") || lowerKey.includes("password") || lowerKey.includes("token") || lowerKey.includes("clave");
  };

  const modalContent = (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm animate-in fade-in duration-200 p-4 overflow-y-auto">
      <div
        role="dialog"
        aria-modal="true"
        data-testid="create-courier-modal"
        className="bg-white dark:bg-[#1a1a1a] w-full max-w-2xl rounded-3xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200 my-auto">

        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border sticky top-0 bg-white/90 dark:bg-[#1a1a1a]/90 backdrop-blur-md z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-ninja-green/10 flex items-center justify-center text-ninja-green">
              <Truck size={20} />
            </div>
            <h2 className="text-xl font-bold text-ninja-dark dark:text-white">Agregar Proveedor Externo</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            aria-label="Cerrar"
            className="p-2 rounded-full hover:bg-ninja-gray dark:hover:bg-[#2d2d2d] text-text-muted transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <form id="create-courier-form" onSubmit={handleSubmit} className="space-y-6">

            {/* Courier Select */}
            <div className="space-y-2">
              <label className="text-sm font-semibold text-text-secondary">Proveedor *</label>
              <select
                value={formData.name}
                onChange={handleCourierChange}
                required
                className="w-full px-4 py-3 bg-ninja-gray/50 dark:bg-[#111] border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-ninja-green focus:border-transparent transition-all dark:text-white"
              >
                <option value="" disabled>Seleccionar...</option>
                {Object.keys(templates).map((courierKey) => (
                  <option key={courierKey} value={courierKey}>
                    {courierKey}
                  </option>
                ))}
              </select>
              {formErrors.name && <p className="text-sm text-danger mt-1">{formErrors.name}</p>}
            </div>

            {/* Config Fields */}
            {formData.name && templates[formData.name] && (
              <div className="bg-ninja-gray/30 dark:bg-[#222] p-5 rounded-2xl space-y-4">
                <h3 className="font-bold text-ninja-dark dark:text-white text-sm">Configuración de Credenciales</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {Object.keys(templates[formData.name]).map((fieldKey) => (
                    <div key={fieldKey} className="space-y-1.5">
                      <label className="text-xs font-semibold text-text-secondary">{formatLabel(fieldKey)} *</label>
                      <input
                        type={isSecretField(fieldKey) ? "password" : "text"}
                        name={fieldKey}
                        value={formData.configuration[fieldKey] || ""}
                        onChange={handleConfigChange}
                        required
                        className={`w-full px-3 py-2 bg-white dark:bg-[#111] border ${formErrors[fieldKey] ? 'border-danger focus:ring-danger' : 'border-border focus:ring-ninja-green'} rounded-xl focus:outline-none focus:ring-2 focus:border-transparent transition-all dark:text-white text-sm`}
                      />
                      {formErrors[fieldKey] && <p className="text-xs text-danger">{formErrors[fieldKey]}</p>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* FEDEX Services */}
            {formData.name === "FEDEX" && formData.services && (
              <div className="border border-dashed border-border p-5 rounded-2xl bg-ninja-gray/10 dark:bg-[#1a1a1a] space-y-4">
                <h3 className="font-bold text-ninja-green text-sm">Enrutamiento Automático por Peso</h3>

                {Object.keys(formData.services).map((serviceKey) => (
                  <div key={serviceKey} className="space-y-2">
                    <h4 className="text-sm font-semibold text-ninja-dark dark:text-white">
                      {serviceKey.replaceAll("_", " ")}
                    </h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-xs text-text-secondary mb-1 block">Peso Mínimo (kg)</label>
                        <input
                          type="number"
                          step="0.1"
                          min="0"
                          value={formData.services[serviceKey].min_weight}
                          onChange={(e) => handleServiceWeightChange(serviceKey, "min_weight", e.target.value)}
                          required
                          className={`w-full px-3 py-2 bg-white dark:bg-[#111] border ${formErrors[`${serviceKey}_min_weight`] ? 'border-danger focus:ring-danger' : 'border-border focus:ring-ninja-green'} rounded-xl focus:outline-none focus:ring-2 focus:border-transparent transition-all dark:text-white text-sm`}
                        />
                        {formErrors[`${serviceKey}_min_weight`] && <p className="text-xs text-danger mt-1">{formErrors[`${serviceKey}_min_weight`]}</p>}
                      </div>
                      <div>
                        <label className="text-xs text-text-secondary mb-1 block">Peso Máximo (kg)</label>
                        <input
                          type="number"
                          step="0.1"
                          min="0"
                          value={formData.services[serviceKey].max_weight}
                          onChange={(e) => handleServiceWeightChange(serviceKey, "max_weight", e.target.value)}
                          required
                          className={`w-full px-3 py-2 bg-white dark:bg-[#111] border ${formErrors[`${serviceKey}_max_weight`] ? 'border-danger focus:ring-danger' : 'border-border focus:ring-ninja-green'} rounded-xl focus:outline-none focus:ring-2 focus:border-transparent transition-all dark:text-white text-sm`}
                        />
                        {formErrors[`${serviceKey}_max_weight`] && <p className="text-xs text-danger mt-1">{formErrors[`${serviceKey}_max_weight`]}</p>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {formErrors.submit && (
              <div className="bg-red-50 dark:bg-red-900/20 text-danger p-4 rounded-xl border border-red-200 dark:border-red-900 text-sm font-medium">
                {formErrors.submit}
              </div>
            )}
          </form>
        </div>

        {/* Footer Actions */}
        <div className="p-6 border-t border-border flex gap-4 bg-ninja-gray/20 dark:bg-[#151515]">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 px-4 py-3 font-bold text-ninja-dark dark:text-white hover:bg-ninja-gray dark:hover:bg-[#2d2d2d] rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={saving}
          >
            Cancelar
          </button>
          <Button
            variant="primary"
            size="lg"
            form="create-courier-form"
            type="submit"
            disabled={!isFormValid}
            loading={saving}
            loadingLabel="Guardando..."
            className="flex-1"
          >
            Guardar Proveedor
          </Button>
        </div>

      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};
